// Angular
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
// RxJS
import { Observable, Subscription } from 'rxjs';
// Object-Path
import * as objectPath from 'object-path';
// Layout
import { LayoutConfigService, MenuConfigService, PageConfigService } from '../../../core/_base/layout';
import { HtmlClassService } from '../html-class.service';
import { LayoutConfig } from '../../../core/_config/layout.config';
import { MenuConfig } from '../../../core/_config/menu.config';
import { PageConfig } from '../../../core/_config/page.config';
// User permissions
import { NgxPermissionsService } from 'ngx-permissions';
import { AuthService } from '../../../../app/core/auth';
import { DevImpersonationService } from '../../../core/_services/dev-impersonation.service';
import { DevRoleSwitcherAuthService } from '../../../core/_services/dev-role-switcher-auth.service';


@Component({
	selector: 'kt-base',
	templateUrl: './base.component.html',
	styleUrls: ['./base.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class BaseComponent implements OnInit, OnDestroy {
	// Public variables
	selfLayout: string;
	asideDisplay: boolean;
	asideSecondary: boolean;
	subheaderDisplay: boolean;
	desktopHeaderDisplay: boolean;
	fitTop: boolean;
	fluid: boolean;
	userRole;
	isDevSwitcherAuthenticated = false;

	// Private properties
	private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
	// private currentUserPermissions$: Observable<Permission[]>;


	/**
	 * Component constructor
	 *
	 * @param layoutConfigService: LayoutConfigService
	 * @param menuConfigService: MenuConfifService
	 * @param pageConfigService: PageConfigService
	 * @param htmlClassService: HtmlClassService
	 * @param store
	 * @param permissionsService
	 */
	constructor(
		private layoutConfigService: LayoutConfigService,
		private menuConfigService: MenuConfigService,
		private pageConfigService: PageConfigService,
		private htmlClassService: HtmlClassService,
		private authService: AuthService,
		private permissionsService: NgxPermissionsService,
		private roleOverrideService: DevImpersonationService,
		private devSwitcherAuthService: DevRoleSwitcherAuthService) {
		this.loadRolesWithPermissions();

		// register configs by demos
		this.layoutConfigService.loadConfigs(new LayoutConfig().configs);
		this.menuConfigService.loadConfigs(new MenuConfig().configs);
		this.pageConfigService.loadConfigs(new PageConfig().configs);

		// setup element classes
		this.htmlClassService.setConfig(this.layoutConfigService.getConfig());

		const subscr = this.layoutConfigService.onConfigUpdated$.subscribe(layoutConfig => {
			// reset body class based on global and page level layout config, refer to html-class.service.ts
			document.body.className = '';
			this.htmlClassService.setConfig(layoutConfig);
		});
		this.unsubscribe.push(subscr);
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		const config = this.layoutConfigService.getConfig();
		this.selfLayout = objectPath.get(config, 'self.layout');
		this.asideDisplay = objectPath.get(config, 'aside.self.display');
		this.subheaderDisplay = objectPath.get(config, 'subheader.display');
		this.desktopHeaderDisplay = objectPath.get(config, 'header.self.fixed.desktop');
		this.fitTop = objectPath.get(config, 'content.fit-top');
		this.fluid = objectPath.get(config, 'content.width') === 'fluid';

		// Subscribe to dev switcher authentication state
		const authSub = this.devSwitcherAuthService.auth$.subscribe(isAuthenticated => {
			this.isDevSwitcherAuthenticated = isAuthenticated;
		});
		this.unsubscribe.push(authSub);

		// Check initial auth state
		this.isDevSwitcherAuthenticated = this.devSwitcherAuthService.checkAuthState();

		// Use role override service to get user attributes
		const userAttr = this.roleOverrideService.getUserAttributes();
		if (userAttr && userAttr['custom:role']) {
			this.userRole = userAttr['custom:role'];
			this.permissionsService.addPermission(this.userRole);
		}

		// Subscribe to impersonation token changes
		const tokenSub = this.roleOverrideService.token$.subscribe(token => {
			const userAttr = this.roleOverrideService.getUserAttributes();
			if (userAttr && userAttr['custom:role']) {
				this.userRole = userAttr['custom:role'];
				this.permissionsService.flushPermissions();
				this.permissionsService.addPermission(this.userRole);
			}
		});
		this.unsubscribe.push(tokenSub);

		this.authService.onUserAttr.subscribe(isStored => {
			if (isStored) {
				const userAttr = this.roleOverrideService.getUserAttributes();
				this.userRole = userAttr ? userAttr['custom:role'] : null;
				if (this.userRole) {
					this.permissionsService.flushPermissions();
					this.permissionsService.addPermission(this.userRole);
				}
			} else {
				this.permissionsService.addPermission('disti');
			}
		});

		// let the layout type change
		const subscr = this.layoutConfigService.onConfigUpdated$.subscribe(cfg => {
			setTimeout(() => {
				this.selfLayout = objectPath.get(cfg, 'self.layout');
			});
		});
		this.unsubscribe.push(subscr);
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.unsubscribe.forEach(sb => sb.unsubscribe());
		this.permissionsService.flushPermissions();
	}

	/**
	 * NGX Permissions, init roles
	 */
	loadRolesWithPermissions() {
		// Role permissions are loaded dynamically based on user attributes
	}
}
